import React, { useState, useCallback, useRef, useEffect } from 'react';
import { loadStripe, Stripe, StripeExpressCheckoutElementConfirmEvent } from '@stripe/stripe-js';
import { Elements, ExpressCheckoutElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Plan } from '../data/plans';
import { SubscriptionPlan } from '../services/credits';
import { API_CONFIG } from '../utils/constants';
import { useTracking } from '../hooks/useTracking';
import { useDeduplicatedPostHog } from '../utils/posthogWrapper';
import PaymentMethodsRow from './PaymentMethodsRow';

// SetupIntent state for a plan
export interface PlanSetupIntent {
  clientSecret: string;
  customerId: string;
  status: 'loading' | 'ready' | 'error';
}

interface PlanCardWithExpressCheckoutProps {
  plan: Plan;
  stripePlan?: SubscriptionPlan;
  setupIntent?: PlanSetupIntent;
  stripePromise: Promise<Stripe | null> | null;
  userId?: string;
  isSelected: boolean;
  isSubscribing: boolean;
  loadingPlans: boolean;
  onFallbackClick: () => void;
  onSuccess: () => void;
  onError: (error: Error) => void;
}

// Inner component that uses Stripe hooks (must be inside Elements provider)
interface ExpressCheckoutInnerProps {
  plan: Plan;
  customerId: string;
  clientSecret: string;
  userId?: string;
  onSuccess: () => void;
  onError: (error: Error) => void;
  onExpressCheckoutReady: (available: boolean, wallets: { applePay: boolean; googlePay: boolean; link: boolean }) => void;
}

function ExpressCheckoutInner({
  plan,
  customerId,
  clientSecret,
  userId,
  onSuccess,
  onError,
  onExpressCheckoutReady,
}: ExpressCheckoutInnerProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { track } = useTracking();
  const posthog = useDeduplicatedPostHog();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const propertiesSetRef = useRef(false);

  // Handle Express Checkout ready - detects available wallets
  // Priority: Apple Pay > Google Pay > Link (Link enabled but lower priority)
  const handleExpressCheckoutReady = useCallback((event: { availablePaymentMethods?: { applePay?: boolean; googlePay?: boolean; link?: boolean } }) => {
    const wallets = {
      applePay: event.availablePaymentMethods?.applePay ?? false,
      googlePay: event.availablePaymentMethods?.googlePay ?? false,
      link: event.availablePaymentMethods?.link ?? false, // Enabled - lower priority than Apple Pay/Google Pay
    };

    // Show express checkout if any wallet is available
    const hasWallets = wallets.applePay || wallets.googlePay || wallets.link;
    onExpressCheckoutReady(hasWallets, wallets);

    track('express_checkout_inline_ready', {
      component: 'PlanCardWithExpressCheckout',
      plan_id: plan.id,
      apple_pay_available: wallets.applePay,
      google_pay_available: wallets.googlePay,
      link_available: wallets.link,
    });

    console.log(`[PlanCard ${plan.id}] Express Checkout ready:`, wallets);
  }, [track, plan.id, onExpressCheckoutReady]);

  // Handle Express Checkout confirmation (after user authenticates with Face ID, etc.)
  const handleExpressCheckoutConfirm = useCallback(async (event: StripeExpressCheckoutElementConfirmEvent) => {
    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    track('express_checkout_inline_started', {
      component: 'PlanCardWithExpressCheckout',
      plan_id: plan.id,
    });

    try {
      // Submit the Express Checkout payment
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setErrorMessage(submitError.message || 'Payment form validation failed');
        track('express_checkout_inline_error', {
          component: 'PlanCardWithExpressCheckout',
          error_type: 'submit_error',
          error_message: submitError.message,
          plan_id: plan.id,
        });
        setIsProcessing(false);
        return;
      }

      // Confirm SetupIntent to collect payment method
      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        clientSecret,
        redirect: 'if_required',
      });

      if (confirmError) {
        setErrorMessage(confirmError.message || 'Payment method collection failed');
        track('express_checkout_inline_error', {
          component: 'PlanCardWithExpressCheckout',
          error_type: 'confirm_error',
          error_message: confirmError.message,
          plan_id: plan.id,
        });
        onError(new Error(confirmError.message || 'Payment method collection failed'));
        setIsProcessing(false);
        return;
      }

      if (!setupIntent || !setupIntent.payment_method) {
        throw new Error('Payment method not collected');
      }

      // Create subscription with the collected payment method
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/stripe/create-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customer_id: customerId,
          payment_method_id: setupIntent.payment_method,
          plan_id: plan.id,
          billing_period: 'monthly',
          user_id: userId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create subscription');
      }

      const data = await response.json();

      if (data.success) {
        const subscriptionStatus = data.status || data.subscription?.status || data.subscription_status;
        const paymentStatus = data.payment_intent?.status || data.payment_status;
        const invoiceStatus = data.latest_invoice?.payment_intent?.status || data.invoice?.payment_intent?.status;
        const subscriptionId = data.subscription_id || data.subscription?.id || data.transaction_id;

        const subscriptionIsActive = subscriptionStatus === 'active' || subscriptionStatus === 'trialing';
        const paymentIntentSucceeded = paymentStatus === 'succeeded' || invoiceStatus === 'succeeded';
        const hasPaymentInfo = paymentStatus || invoiceStatus;
        const paymentSucceeded = hasPaymentInfo
          ? (paymentIntentSucceeded && subscriptionIsActive)
          : subscriptionIsActive;

        if (!paymentSucceeded) {
          track('express_checkout_inline_error', {
            component: 'PlanCardWithExpressCheckout',
            error_type: 'payment_verification_failed',
            subscription_status: subscriptionStatus || 'unknown',
            payment_status: paymentStatus || 'unknown',
            plan_id: plan.id,
          });
          throw new Error('Payment verification failed. Please try again.');
        }

        // Track success
        track('express_checkout_inline_success', {
          component: 'PlanCardWithExpressCheckout',
          plan_id: plan.id,
          subscription_id: subscriptionId,
          subscription_status: subscriptionStatus,
        });

        // Track subscribe event for initial purchases
        const conversionValuesByPlan: Record<string, number> = {
          'basic': 20,
          'pro': 40,
          'premium': 80,
        };
        const conversionValue = conversionValuesByPlan[plan.id.toLowerCase()] || 0;

        track('subscribe', {
          transaction_id: subscriptionId || `sub_${Date.now()}`,
          plan_id: plan.id,
          plan_name: plan.name,
          currency: 'USD',
          value: conversionValue,
          billing_period: 'monthly',
          product_type: 'subscription',
          payment_method: 'express_checkout_inline',
          user_id: userId || '',
          subscription_status: subscriptionStatus,
          timestamp: new Date().toISOString(),
        });

        // Update user properties
        if (posthog && userId && !propertiesSetRef.current) {
          posthog.setPersonProperties({
            has_payment_method: true,
            subscription_plan: plan.id,
            subscription_billing_period: 'monthly',
            subscription_status: subscriptionStatus || 'active',
            last_purchase_date: new Date().toISOString(),
            payment_method_type: 'express_checkout_inline',
          });
          propertiesSetRef.current = true;
        }

        onSuccess();
      } else {
        throw new Error(data.error || data.message || 'Subscription creation failed');
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('An unexpected error occurred');
      setErrorMessage(error.message);
      track('express_checkout_inline_error', {
        component: 'PlanCardWithExpressCheckout',
        error_type: 'unexpected_error',
        error_message: error.message,
        plan_id: plan.id,
      });
      onError(error);
    } finally {
      setIsProcessing(false);
    }
  }, [stripe, elements, clientSecret, customerId, plan.id, plan.name, userId, posthog, track, onSuccess, onError]);

  // Handle Express Checkout cancel (user closed wallet sheet)
  const handleExpressCheckoutCancel = useCallback(() => {
    track('express_checkout_inline_cancelled', {
      component: 'PlanCardWithExpressCheckout',
      plan_id: plan.id,
    });
    console.log(`[PlanCard ${plan.id}] Express Checkout cancelled by user`);
  }, [track, plan.id]);

  return (
    <div className="space-y-3">
      {errorMessage && (
        <div className="p-2 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-xs">
          {errorMessage}
        </div>
      )}

      {isProcessing && (
        <div className="text-center text-white/60 text-sm animate-pulse">
          Processing payment...
        </div>
      )}

      <ExpressCheckoutElement
        onReady={handleExpressCheckoutReady}
        onConfirm={handleExpressCheckoutConfirm}
        onCancel={handleExpressCheckoutCancel}
        options={{
          buttonType: { applePay: 'plain', googlePay: 'plain' },
          buttonTheme: { applePay: 'black', googlePay: 'black' },
          buttonHeight: 44,
          layout: { maxColumns: 2, maxRows: 1 },
          // Logo only (no text) - Priority: Apple Pay > Google Pay > Link
          // All enabled via SetupIntent payment_method_types: ['card', 'link']
        }}
      />
    </div>
  );
}

export default function PlanCardWithExpressCheckout({
  plan,
  stripePlan,
  setupIntent,
  stripePromise,
  userId,
  isSelected,
  isSubscribing,
  loadingPlans,
  onFallbackClick,
  onSuccess,
  onError,
}: PlanCardWithExpressCheckoutProps) {
  const [expressCheckoutReady, setExpressCheckoutReady] = useState(false);
  const [availableWallets, setAvailableWallets] = useState<{
    applePay: boolean;
    googlePay: boolean;
    link: boolean;
  }>({ applePay: false, googlePay: false, link: false });

  const handleExpressCheckoutReady = useCallback((available: boolean, wallets: { applePay: boolean; googlePay: boolean; link: boolean }) => {
    setExpressCheckoutReady(available);
    setAvailableWallets(wallets);
  }, []);

  // Slashed prices for Valentine's Day sale
  const slashedPricesMonthly: Record<string, number> = {
    'basic': 33,
    'pro': 67,
    'premium': 133,
  };
  const slashedPrice = slashedPricesMonthly[plan.id];

  // Check if we can show express checkout
  const canShowExpressCheckout =
    setupIntent?.status === 'ready' &&
    setupIntent.clientSecret &&
    stripePromise;

  return (
    <div
      className={`bg-white/5 border rounded-2xl p-4 sm:p-6 flex flex-col transition-all ${
        isSelected
          ? 'border-white/50 bg-white/10 shadow-lg'
          : 'border-white/20 hover:border-white/30 hover:bg-white/5'
      }`}
    >
      {/* Header Section */}
      <div className="flex items-center gap-3 mb-4">
        <h3 className="text-2xl sm:text-3xl font-bold text-white">{plan.name}</h3>
        {plan.popular && (
          <div className="inline-block bg-blue-500/20 border border-blue-500/50 text-blue-400 px-3 py-1 rounded-full text-xs font-semibold">
            {plan.badgeText}
          </div>
        )}
      </div>

      {/* Price Section */}
      <div className="mb-4">
        <div className="flex items-baseline gap-2">
          <span className="text-4xl font-bold text-white">
            {loadingPlans ? (
              '...'
            ) : (() => {
              if (!stripePlan) {
                return '...';
              }
              const displayPrice = stripePlan.price_monthly_usd;
              return `$${displayPrice.toFixed(0)}`;
            })()}
          </span>
          <span className="text-white/70">/month</span>
          {!loadingPlans && slashedPrice && (
            <span className="text-2xl font-bold text-white/40 line-through ml-1">
              ${slashedPrice}
            </span>
          )}
        </div>
        <p className="text-xs text-white/60 mt-2 text-left">
          Billed monthly
        </p>
      </div>

      {/* Features List */}
      <div className="flex-1">
        <ul className="space-y-3 mb-6 text-left">
          {plan.features.map((feature, idx) => (
            <li key={idx} className="flex items-start gap-2">
              <svg className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-white/80 text-sm">{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Footer Section */}
      <div className="mt-auto pt-4 border-t border-white/10">
        <PaymentMethodsRow variant="compact" align="center" />

        {/* Select Plan Button (opens modal) */}
        <button
          onClick={onFallbackClick}
          disabled={isSubscribing}
          className="w-full bg-[#F9F7EA] text-[#0f1218] px-6 py-3 rounded-full font-semibold hover:bg-[#E7D7B1] transition-colors mb-3 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubscribing && isSelected ? 'Processing...' : 'Select Plan'}
        </button>

        {/* Express Checkout Section - Only show when available */}
        {canShowExpressCheckout && (
          <>
            {/* Divider - only show when express checkout is ready and has wallets */}
            {expressCheckoutReady && (
              <div className="flex items-center gap-3 my-3">
                <div className="flex-1 h-px bg-white/20"></div>
                <span className="text-white/50 text-xs">or</span>
                <div className="flex-1 h-px bg-white/20"></div>
              </div>
            )}

            {/* Express Checkout Element wrapped in Elements provider */}
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret: setupIntent.clientSecret,
                appearance: {
                  theme: 'night',
                  variables: {
                    colorPrimary: '#ffffff',
                    colorBackground: '#000000',
                    colorText: '#ffffff',
                    colorDanger: '#ef4444',
                    fontFamily: 'system-ui, sans-serif',
                    spacingUnit: '4px',
                    borderRadius: '8px',
                  },
                },
              }}
            >
              <ExpressCheckoutInner
                plan={plan}
                customerId={setupIntent.customerId}
                clientSecret={setupIntent.clientSecret}
                userId={userId}
                onSuccess={onSuccess}
                onError={onError}
                onExpressCheckoutReady={handleExpressCheckoutReady}
              />
            </Elements>
          </>
        )}

        {/* Loading state for SetupIntent */}
        {setupIntent?.status === 'loading' && (
          <div className="text-center text-white/40 text-xs mt-2 animate-pulse">
            Loading payment options...
          </div>
        )}
      </div>
    </div>
  );
}
